/* eslint-disable jsx-a11y/label-has-associated-control */

import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
} from 'recharts';
import { notify } from '../../Shared/toast';

function Accounts() {
  // State for different functionalities
  const [activeTab, setActiveTab] = useState('reports');
  const [timeRange, setTimeRange] = useState('monthly');
  const [expenses, setExpenses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [newExpense, setNewExpense] = useState({
    description: '',
    amount: '',
    category: 'supplies',
    vendor: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [newPayment, setNewPayment] = useState({
    vendorId: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    method: 'bank',
  });

  // Sample data - in a real app, this would come from an API
  useEffect(() => {
    // Mock data for demonstration
    setExpenses([
      {
        id: 1, description: 'Spices from Local Market', amount: 12500, category: 'ingredients', vendor: 'Chittagong Spice House', date: '2023-06-15',
      },
      {
        id: 2, description: 'Kitchen Equipment', amount: 35000, category: 'equipment', vendor: 'Dhaka Restaurant Supplies', date: '2023-06-20',
      },
      {
        id: 3, description: 'Monthly Utilities', amount: 18500, category: 'utilities', vendor: 'DESCO', date: '2023-06-25',
      },
    ]);

    setVendors([
      {
        id: 1, name: 'Chittagong Spice House', contact: '01812345678', balance: 12500,
      },
      {
        id: 2, name: 'Dhaka Restaurant Supplies', contact: '01798765432', balance: 35000,
      },
      {
        id: 3, name: 'DESCO', contact: '16123', balance: 18500,
      },
    ]);

    setTransactions([
      {
        id: 1, type: 'income', description: 'Dine-in Sales', amount: 125000, date: '2023-06-15',
      },
      {
        id: 2, type: 'income', description: 'Delivery Sales', amount: 85000, date: '2023-06-16',
      },
      {
        id: 3, type: 'expense', description: 'Staff Salaries', amount: 75000, date: '2023-06-20',
      },
    ]);
  }, []);

  // Calculate financial reports
  const calculateReports = () => {
    const income = transactions
      .filter((t) => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);

    const expensesTotal = transactions
      .filter((t) => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);

    const profit = income - expensesTotal;

    // Bangladeshi VAT calculation (15%)
    const tax = profit * 0.15;

    return {
      income, expenses: expensesTotal, profit, tax,
    };
  };

  const {
    income, expenses: expensesTotal, profit, tax,
  } = calculateReports();

  // Chart data
  const salesData = [
    { name: 'Week 1', sales: 120000 },
    { name: 'Week 2', sales: 190000 },
    { name: 'Week 3', sales: 150000 },
    { name: 'Week 4', sales: 180000 },
  ];

  const expenseCategories = [
    { name: 'Ingredients', value: 45 },
    { name: 'Salaries', value: 30 },
    { name: 'Utilities', value: 15 },
    { name: 'Equipment', value: 10 },
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Handle form submissions
  const handleAddExpense = (e) => {
    e.preventDefault();
    const newId = expenses.length > 0 ? Math.max(...expenses.map((e) => e.id)) + 1 : 1;
    setExpenses([...expenses, { ...newExpense, id: newId, amount: parseFloat(newExpense.amount) }]);
    setNewExpense({
      description: '',
      amount: '',
      category: 'supplies',
      vendor: '',
      date: new Date().toISOString().split('T')[0],
    });
  };

  const handleMakePayment = (e) => {
    e.preventDefault();
    // In a real app, this would connect to bank API
    notify.success(`Payment of BDT ${newPayment.amount} processed successfully`);
    setNewPayment({
      vendorId: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      method: 'bank',
    });
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 md:mb-6">Accounts Management</h1>

      {/* Navigation Tabs - Fixed for mobile */}
      <div className="mb-4 md:mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-2 overflow-x-auto pb-1 -mb-px scrollbar-hide">
            <button
              type="button"
              className={`py-2 px-3 md:px-4 font-medium text-sm md:text-base whitespace-nowrap ${activeTab === 'reports' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('reports')}
            >
              Financial Reports
            </button>
            <button
              type="button"
              className={`py-2 px-3 md:px-4 font-medium text-sm md:text-base whitespace-nowrap ${activeTab === 'expenses' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('expenses')}
            >
              Expense Tracking
            </button>
            <button
              type="button"
              className={`py-2 px-3 md:px-4 font-medium text-sm md:text-base whitespace-nowrap ${activeTab === 'vendors' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('vendors')}
            >
              Vendor Payments
            </button>
            <button
              type="button"
              className={`py-2 px-3 md:px-4 font-medium text-sm md:text-base whitespace-nowrap ${activeTab === 'tax' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('tax')}
            >
              Tax Calculations
            </button>
          </nav>
        </div>
      </div>

      {/* Financial Reports Tab */}
      {activeTab === 'reports' && (
      <div>
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 md:mb-6">
          <h2 className="text-xl font-semibold text-gray-700 mb-2 md:mb-0">Financial Reports</h2>
          <div className="flex space-x-2">
            <button
              type="button"
              className={`px-3 py-1.5 md:px-4 md:py-2 rounded text-sm md:text-base ${timeRange === 'daily' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              onClick={() => setTimeRange('daily')}
            >
              Daily
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 md:px-4 md:py-2 rounded text-sm md:text-base ${timeRange === 'monthly' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              onClick={() => setTimeRange('monthly')}
            >
              Monthly
            </button>
          </div>
        </div>

        {/* Summary Cards - Adjusted for mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm md:text-base font-medium">Total Income</h3>
            <p className="text-lg md:text-2xl font-bold text-green-600">
              BDT
              {' '}
              {income.toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm md:text-base font-medium">Total Expenses</h3>
            <p className="text-lg md:text-2xl font-bold text-red-600">
              BDT
              {' '}
              {expensesTotal.toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm md:text-base font-medium">Profit</h3>
            <p className="text-lg md:text-2xl font-bold text-blue-600">
              BDT
              {' '}
              {profit.toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-gray-500 text-sm md:text-base font-medium">Tax (15%)</h3>
            <p className="text-lg md:text-2xl font-bold text-purple-600">
              BDT
              {' '}
              {tax.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Charts - Responsive layout */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Monthly Sales</h3>
            <div className="w-full h-64 md:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={salesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="sales" fill="#8884d8" name="Sales (BDT)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Expense Categories</h3>
            <div className="w-full h-64 md:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={expenseCategories}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {expenseCategories.map((entry, index) => (
                      <Cell key={`cell-${entry.name}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <h3 className="text-lg font-semibold p-4 bg-gray-100">Recent Transactions</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount (BDT)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.date}</td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{transaction.description}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${transaction.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {transaction.type}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.amount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}

      {/* Expense Tracking Tab */}
      {activeTab === 'expenses' && (
      <div>
        <h2 className="text-xl font-semibold text-gray-700 mb-4 md:mb-6">Expense Tracking</h2>

        {/* Add Expense Form */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow mb-6 md:mb-8">
          <h3 className="text-lg font-medium mb-3 md:mb-4">Add New Expense</h3>
          <form onSubmit={handleAddExpense}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 mb-4">
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Description
                </label>
                <input
                  id="description"
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:text-base mt-1"
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({
                    ...newExpense,
                    description: e.target.value,
                  })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (BDT)
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:text-base mt-1"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                    required
                  />
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:text-base mt-1"
                    value={newExpense.category}
                    onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                  >
                    <option value="supplies">Kitchen Supplies</option>
                    <option value="ingredients">Ingredients</option>
                    <option value="equipment">Equipment</option>
                    <option value="utilities">Utilities</option>
                    <option value="salaries">Salaries</option>
                    <option value="other">Other</option>
                  </select>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vendor/Supplier
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:text-base mt-1"
                    value={newExpense.vendor}
                    onChange={(e) => setNewExpense({ ...newExpense, vendor: e.target.value })}
                  />
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:text-base mt-1"
                    value={newExpense.date}
                    onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                  />
                </label>
              </div>
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm md:text-base"
            >
              Add Expense
            </button>
          </form>
        </div>

        {/* Expenses List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <h3 className="text-lg font-semibold p-3 md:p-4 bg-gray-100">Recent Expenses</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                  <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount (BDT)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expenses.map((expense) => (
                  <tr key={expense.id}>
                    <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">{expense.date}</td>
                    <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm font-medium text-gray-900">{expense.description}</td>
                    <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{expense.category}</td>
                    <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">{expense.vendor}</td>
                    <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">{expense.amount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}

      {/* Vendor Payments Tab */}
      {activeTab === 'vendors' && (
      <div>
        <h2 className="text-xl font-semibold text-gray-700 mb-4 md:mb-6">Vendor Payments</h2>

        {/* Make Payment Form */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow mb-6 md:mb-8">
          <h3 className="text-lg font-medium mb-3 md:mb-4">Make Payment to Vendor</h3>
          <form onSubmit={handleMakePayment}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vendor
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:text-base mt-1"
                    value={newPayment.vendorId}
                    onChange={(e) => setNewPayment({ ...newPayment, vendorId: e.target.value })}
                    required
                  >
                    <option value="">Select Vendor</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.name}
                        {' '}
                        (BDT
                        {' '}
                        {vendor.balance.toLocaleString()}
                        )
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (BDT)
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:text-base mt-1"
                    value={newPayment.amount}
                    onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                    required
                  />
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:text-base mt-1"
                    value={newPayment.method}
                    onChange={(e) => setNewPayment({ ...newPayment, method: e.target.value })}
                  >
                    <option value="bank">Bank Transfer</option>
                    <option value="bkash">bKash</option>
                    <option value="nagad">Nagad</option>
                    <option value="rocket">Rocket</option>
                    <option value="cash">Cash</option>
                  </select>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm md:text-base mt-1"
                    value={newPayment.date}
                    onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                  />
                </label>
              </div>
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm md:text-base"
            >
              Process Payment
            </button>
          </form>
        </div>

        {/* Vendors List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <h3 className="text-lg font-semibold p-3 md:p-4 bg-gray-100">Vendors & Suppliers</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance (BDT)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vendors.map((vendor) => (
                  <tr key={vendor.id}>
                    <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm font-medium text-gray-900">{vendor.name}</td>
                    <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">{vendor.contact}</td>
                    <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">{vendor.balance.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}

      {/* Tax Calculations Tab */}
      {activeTab === 'tax' && (
      <div>
        <h2 className="text-xl font-semibold text-gray-700 mb-4 md:mb-6">Tax Calculations</h2>

        <div className="bg-white p-4 md:p-6 rounded-lg shadow mb-6 md:mb-8">
          <h3 className="text-lg font-medium mb-3 md:mb-4">Bangladesh VAT Calculation</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Tax Summary</h4>
              <div className="space-y-2 md:space-y-3">
                <div className="flex justify-between border-b pb-2">
                  <span className="text-sm md:text-base">Taxable Income:</span>
                  <span className="font-medium text-sm md:text-base">
                    BDT
                    {' '}
                    {profit.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-sm md:text-base">VAT Rate (15%):</span>
                  <span className="font-medium text-sm md:text-base">15%</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-sm md:text-base">VAT Amount Due:</span>
                  <span className="font-medium text-red-600 text-sm md:text-base">
                    BDT
                    {' '}
                    {tax.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-700 mb-2">Payment Information</h4>
              <div className="space-y-2 md:space-y-3">
                <div className="flex justify-between border-b pb-2">
                  <span className="text-sm md:text-base">Tax Identification Number (TIN):</span>
                  <span className="font-medium text-sm md:text-base">123-456-789</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-sm md:text-base">Due Date:</span>
                  <span className="font-medium text-sm md:text-base">15th of next month</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-sm md:text-base">Payment Methods:</span>
                  <span className="font-medium text-sm md:text-base">Bank Transfer, bKash</span>
                </div>
              </div>

              <button type="button" className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm md:text-base">
                Generate Tax Report
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 md:p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium mb-3 md:mb-4">Tax Payment History</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                  <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount (BDT)</th>
                  <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Date</th>
                  <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt No.</th>
                  <th className="px-4 py-2 md:px-6 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">May 2023</td>
                  <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">22,500</td>
                  <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">2023-06-14</td>
                  <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">TXN456789</td>
                  <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Paid
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">April 2023</td>
                  <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">18,750</td>
                  <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">2023-05-15</td>
                  <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">TXN123456</td>
                  <td className="px-4 py-2 md:px-6 md:py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Paid
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

export default Accounts;
